import { numberHelpers } from '@approvalmax/utils';
import { uniq } from 'lodash';
import { domain } from 'modules/data';
import { useNetSuiteAttachmentsFieldSettings, useNetSuiteJournalEntryFieldSettings } from 'shared/hooks';

import { CustomFieldsState } from '../data/types/CustomFields';
import { NetSuiteJournalEntryLineExpanded } from '../data/types/NetSuiteJournalEntryLineExpanded';
import { getCustomFieldsTransfer, validateCustomFields } from '../helpers';
import { isFieldMandatory, isMandatoryFieldEmpty } from '../NetSuiteRequestForm/NetSuiteRequestForm.helpers';
import { messages } from './NetSuiteJournalEntryRequestForm.messages';
import { NetSuiteJournalEntryFields } from './NetSuiteJournalEntryRequestForm.types';

export const mapJournalEntryItemLinesTransfer = (itemLines: NetSuiteJournalEntryLineExpanded[]) =>
    itemLines.map((itemLine) => ({
        accountId: itemLine.account?.id ?? null,
        memo: itemLine.memo,
        taxAccountId: itemLine.taxAccount?.id ?? null,
        amortizationSchedule: itemLine.amortizationSchedule?.id ?? null,
        amortizationStartDate: itemLine.amortizationStartDate,
        amortizationEndDate: itemLine.amortizationEndDate,
        amortizationResidual: itemLine.amortizationResidual,
        debitAmount: itemLine.debitAmount,
        creditAmount: itemLine.creditAmount,
        taxAmount: itemLine.taxAmount,
        departmentId: itemLine.department?.id ?? null,
        classId: itemLine.class?.id ?? null,
        locationId: itemLine.location?.id ?? null,
        lineNumber: itemLine.lineNumber,
        taxCodeId: itemLine.taxCode?.id ?? null,
        customFields: getCustomFieldsTransfer(itemLine.customFields),
        tax2Amount: null,
    }));

const getItemListValidationErrors = (data: {
    lines: NetSuiteJournalEntryLineExpanded[];
    fieldSettings: ReturnType<typeof useNetSuiteJournalEntryFieldSettings>;
    customFieldsList: domain.Field[];
    workflowId: string;
    errors: string[];
}) => {
    const { lines, fieldSettings, customFieldsList, workflowId, errors } = data;

    lines.forEach((line) => {
        if (
            !line.account ||
            isMandatoryFieldEmpty(line.memo, fieldSettings.itemsMemo) ||
            isMandatoryFieldEmpty(line.taxCode, fieldSettings.itemsTaxCode) ||
            isMandatoryFieldEmpty(line.taxAccount, fieldSettings.itemsTaxAccount) ||
            isMandatoryFieldEmpty(line.department, fieldSettings.itemsDepartment) ||
            isMandatoryFieldEmpty(line.location, fieldSettings.itemsLocation) ||
            isMandatoryFieldEmpty(line.class, fieldSettings.itemsClass) ||
            isMandatoryFieldEmpty(line.customer, fieldSettings.itemsCustomer) ||
            isMandatoryFieldEmpty(line.amortizationSchedule, fieldSettings.itemsAmortizationSchedule) ||
            isMandatoryFieldEmpty(line.amortizationStartDate, fieldSettings.itemsAmortizationStartDate) ||
            isMandatoryFieldEmpty(line.amortizationEndDate, fieldSettings.itemsAmortizationEndDate) ||
            isMandatoryFieldEmpty(line.amortizationResidual, fieldSettings.itemsAmortizationResidual)
        ) {
            errors.push(messages.mandatoryFieldsError);
        }

        validateCustomFields({
            customFieldsValues: line.customFields || [],
            customFieldsLevels: ['Lines', 'Header And Lines'],
            customFieldsList,
            workflowId,
            errors,
        });
    });
};

export const getJournalEntryValidationErrors = (data: {
    fields: Partial<NetSuiteJournalEntryFields>;
    customFieldsValues: CustomFieldsState;
    customFieldsList: domain.Field[];
    workflowId: string;
    fieldSettings: ReturnType<typeof useNetSuiteJournalEntryFieldSettings>;
    companyCurrency: string;
    requestCurrencyISOCode: string | null;
    defaultCurrencyRate: number | undefined;
    lines: NetSuiteJournalEntryLineExpanded[];
    attachmentsFieldSettings: ReturnType<typeof useNetSuiteAttachmentsFieldSettings>;
    attachments: domain.RequestAttachment[];
}) => {
    const {
        fields,
        customFieldsValues,
        customFieldsList,
        workflowId,
        companyCurrency,
        requestCurrencyISOCode,
        fieldSettings,
        defaultCurrencyRate,
        lines,
        attachmentsFieldSettings,
        attachments,
    } = data;

    const errors: string[] = [];

    if (
        !fields.transactionDate ||
        isMandatoryFieldEmpty(fields.reference, fieldSettings.reference) ||
        isMandatoryFieldEmpty(fields.class, fieldSettings.class) ||
        isMandatoryFieldEmpty(fields.department, fieldSettings.department) ||
        isMandatoryFieldEmpty(fields.location, fieldSettings.location) ||
        isMandatoryFieldEmpty(fields.memo, fieldSettings.memo) ||
        isMandatoryFieldEmpty(fields.reversalDate, fieldSettings.reversalDate)
    ) {
        errors.push(messages.mandatoryFieldsError);
    }

    if (isFieldMandatory(attachmentsFieldSettings.attachments) && attachments.length === 0) {
        errors.push(messages.mandatoryFieldsError);
    }

    if (
        requestCurrencyISOCode !== companyCurrency &&
        !numberHelpers.isNumber(fields.exchangeRate) &&
        !numberHelpers.isNumber(defaultCurrencyRate)
    ) {
        errors.push(messages.exchangeRateError);
    }

    getItemListValidationErrors({ lines, fieldSettings, customFieldsList, workflowId, errors });

    validateCustomFields({
        customFieldsValues: Object.values(customFieldsValues),
        customFieldsLevels: ['Header', 'Header And Lines'],
        customFieldsList,
        workflowId,
        errors,
    });

    return uniq(errors);
};
